import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    dir: 'ltr',
    nav: {
      home: 'Home',
      about: 'About',
      services: 'Services',
      portfolio: 'Portfolio',
      caseStudies: 'Case Studies',
      blog: 'Blog',
      careers: 'Careers',
      contact: 'Contact',
      consultation: 'Free Consultation',
      more: 'More',
    },
    home: {
      badge: 'Powered by AI',
      title1: 'Grow Faster with',
      titleHighlight: 'AI-Powered',
      title2: 'Marketing',
      subtitle: 'ZS Digitals helps brands scale with powerful AI content, engaging UGC videos, and result-driven advertising strategies.',
      ctaPrimary: 'Get Free Consultation',
      ctaSecondary: 'View Portfolio',
      stat1: 'Years Experience',
      stat2: 'Avg. ROI Increase',
      stat3: 'Ad Impressions',
      stat4: 'Platforms',
      servicesTitle: 'Our Core Services',
      servicesTag: 'What We Do',
      svc1: 'Social Media Management',
      svc1Desc: '25 pieces/month. Professional reels, posts, copywriting & daily story management.',
      svc2: 'AI Ad Creatives',
      svc2Desc: '25 AI-generated video ads/month. Competitor analysis, strategy & tailored copywriting.',
      svc3: 'Paid Advertisement',
      svc3Desc: 'The complete growth package. Organic + AI creatives + aggressive multi-platform media buying.',
      learnMore: 'Learn more',
      platformsTag: 'Platforms We Work On',
      whyTag: 'Why ZS Digitals',
      whyTitle: 'Results That Speak for Themselves',
      whySubtitle: 'We combine cutting-edge AI technology with proven marketing strategies to deliver measurable results for every client.',
      whyPoints: ['AI-powered content production', 'Data-driven marketing approach', 'Expertise across multiple platforms', 'Focus on real business growth'],
      caseTag: 'Case Studies',
      caseTitle: 'Real Results. Real Growth.',
      viewAll: 'View all',
      testimonialsTag: 'Testimonials',
      testimonialsTitle: 'What Our Clients Say',
      ctaTitle: 'Ready to Grow Your Business?',
      ctaSubtitle: 'Start your journey with ZS Digitals today and experience the power of AI-driven marketing.',
      ctaBtn: 'Start Today',
    },
    about: {
      tag: 'About Us',
      title1: 'We Build Brands That',
      titleHighlight: 'Dominate',
      subtitle: 'ZS Digitals is a modern digital marketing agency focused on helping brands grow using AI-driven content and smart advertising strategies.',
      storyTag: 'Our Story',
      storyTitle: 'From Vision to Reality',
      storyP1: 'Founded with a belief that marketing should be smarter, not harder, ZS Digitals emerged at the intersection of artificial intelligence and creative storytelling.',
      storyP2: 'We recognized early that the future of marketing lies in leveraging AI to create personalized, high-converting content at scale. Today, we serve over 200 clients across the UK and beyond.',
      storyP3: 'Our team of creative minds, data scientists, and marketing strategists work together to deliver campaigns that don\'t just look great, they perform.',
      missionTitle: 'Our Mission',
      missionText: 'To help businesses grow faster with innovative marketing solutions powered by AI. We are committed to delivering measurable results that drive real business growth.',
      visionTitle: 'Our Vision',
      visionText: 'To become a leading agency known for creativity, innovation, and measurable results. We envision a world where every brand has access to enterprise-level marketing powered by AI.',
      valuesTag: 'Our Values',
      valuesTitle: 'What Drives Us',
      values: [
        { title: 'Innovation', desc: 'We push boundaries with cutting-edge AI and creative solutions.' },
        { title: 'Transparency', desc: 'Honest communication and clear reporting at every step.' },
        { title: 'Performance', desc: 'Every strategy is built around measurable results.' },
        { title: 'Creativity', desc: 'We blend art and data to create memorable brand experiences.' },
      ],
      teamTag: 'The Team',
      teamTitle: 'Meet Our Experts',
    },
    services: {
      tag: 'Our Services',
      title1: 'AI-Powered Content.',
      titleHighlight: 'Real Growth.',
      subtitle: 'From high-volume AI video production to full-stack paid advertising — we deliver the content engine and media buying expertise your brand needs to dominate.',
      whatsIncluded: "What's Included",
      getStarted: 'Get Started',
      completePackage: 'Complete Growth Package',
      platformsTag: 'Platforms',
      platformsTitle: 'We Scale Across All Major Platforms',
      ctaTitle: 'Ready to Scale with AI?',
      ctaSubtitle: 'Let\'s discuss which package is right for your brand and start building your content engine.',
      ctaBtn: 'Book a Strategy Call',
      svc1: {
        title: 'Social Media Management',
        tagline: 'End-to-end organic social growth powered by high-volume, professional content.',
        items: ['25 pieces of content per month', '20 Professional Reels', '5 High-Quality Posts', 'Full copywriting for every piece', 'Daily management of Highlights & Stories', 'Comprehensive market research & strategy'],
      },
      svc2: {
        title: 'AI Ad Creatives',
        tagline: 'High-converting, AI-generated video creatives engineered for performance marketing.',
        items: ['25 Professional Ad Creatives per month', 'Deep competitor analysis', 'Market research & full marketing strategy', 'Tailored ad copywriting for every creative', 'Performance-optimized formats', 'Multi-platform creative variations'],
      },
      svc3: {
        title: 'Paid Advertisement',
        tagline: 'The ultimate scaling solution. We combine organic content, AI creatives, and aggressive media buying.',
        items: ['Everything in Services 01 & 02 included', 'Dedicated paid ad management', 'Meta (Instagram/Facebook) campaigns', 'Google Ads campaigns', 'TikTok & Snapchat campaigns', '25 exclusive ad creatives for paid campaigns', 'Complete brand/visual identity overhaul', 'New logo design if required'],
      },
    },
    portfolio: {
      tag: 'Portfolio',
      title1: 'AI Video',
      titleHighlight: 'Production',
      title2: 'Showcase',
      subtitle: 'Real AI-generated video creatives we produced for clients. Hover to preview, click to watch full.',
      industry: 'Industry',
      style: 'Style',
      all: 'All',
      aiGenerated: 'AI Generated',
      noResults: 'No videos match the selected filters.',
    },
    caseStudies: {
      tag: 'Case Studies',
      title1: 'Proven ROI.',
      titleHighlight: 'Measurable Growth.',
      subtitle: 'See how our AI-powered ad creatives and media buying strategies have delivered real returns for real brands.',
      challenge: 'The Problem',
      aiSolution: 'What We Did',
      results: 'Results',
      getResults: 'Get Results Like This',
      ctaTitle: 'Want These Results for Your Brand?',
      ctaSubtitle: 'Let us show you how AI-generated creatives can transform your advertising performance.',
      ctaBtn: 'Book a Strategy Call',
      clientStudy: 'Client Case Study',
      labelClient: 'Client',
      labelIndustry: 'Industry',
      labelLocation: 'Location',
      labelService: 'Service',
      labelCatalog: 'Catalog SKUs',
      labelRating: 'Customer Rating',
      labelFormat: 'Ad Format',
      labelBookingRate: 'Booking Rate',
      labelCpc: 'Cost Per Click',
      labelFullLaunch: 'Full Launch',
      labelUnitLead: 'Unit B2B Lead',
      labelFullStack: 'Full Stack',
      labelFullStackServices: 'Services',
      amxClient: 'AMX Custom Print',
      amxTagline: 'Full-Stack Brand Launch',
      dhz: {
        headline: "How We Turned DHZ-Proshop's Meta Ads Into a High-Performing Sales Engine",
        client: 'DHZ-Proshop',
        industry: 'Premium Online Retail — Professional Tools & Industrial Materials',
        location: 'Belgium',
        service: 'Meta Ads — Video Creative Strategy & Production',
        problem: "DHZ-Proshop had a massive catalog of 40,000+ products and a 9.6/10 customer rating — but their Meta ads weren't converting. Static product images failed to communicate their brand expertise, logistics speed, or the scale of their inventory.",
        whatWeDid: "We replaced all static creatives with high-fidelity, narrative-driven video ads built around three pillars: human connection (a spokesperson walkthrough of the warehouse), logistics urgency (\"Order before 16:00, shipped today\"), and social proof (their 9.6/10 rating woven into the visuals). Deployed across Meta's full-funnel strategy.",
        results: 'Significant increase in daily transaction volume. Higher CTR from video storytelling vs static images. ROAS stabilized at a consistently profitable level. Longer view times and stronger brand recall.',
        statSku: '40K+',
        statSkuLabel: 'Catalog SKUs',
        statRating: '9.6/10',
        statRatingLabel: 'Customer Rating',
        statFormat: 'Meta Ads',
        statFormatLabel: 'Ad Format',
      },
      gadget: {
        headline: 'How We Helped The Gadget Guys Cut Through Digital Noise and Drive Real Sales',
        client: 'The Gadget Guys',
        industry: 'Tech Services & Retail — IT Support, Custom Gaming Builds, Computer Repair',
        location: 'Fort Washington',
        service: 'Meta Ads — Video Creative Strategy & Production',
        problem: "The Gadget Guys were running Meta Ads with generic stock photos of computers. The creatives were cluttered, had no personal connection, and weren't converting — leading to wasted ad spend and stagnant sales.",
        whatWeDid: 'We shifted to high-energy, narrative-driven video content featuring real technicians on actual builds. Used dynamic on-screen callouts for specific services (Virus Cleanup, Data Recovery, Custom Gaming Builds), highlighted their physical location and phone number for local lead generation, and built the videos around a clear problem-solution framework.',
        results: "Significant spike in conversion rates for service bookings and hardware sales. Meta's algorithm favored the video content, lowering CPC. Improved ROAS across the account. Increased foot traffic and direct phone inquiries in Fort Washington.",
        statConv: '↑ Conv.',
        statConvLabel: 'Booking Rate',
        statCpc: '↓ CPC',
        statCpcLabel: 'Cost Per Click',
        statFormat: 'Meta Ads',
        statFormatLabel: 'Ad Format',
      },
      amx: {
        headline: 'AMX Custom Print — Full-Stack Brand Launch in Under 10 Hours',
        problem: 'The client needed a complete digital brand launch from scratch — including visual identity, social media presence, and ad creatives — and required an ultra-fast turnaround without sacrificing quality or performance.',
        whatWeDid: 'ZS Digitals executed a complete, full-stack launch in under 10 hours. We designed their new logo, built out their social strategy, and generated a diverse suite of AI video creatives — from UGC try-ons to 3D VFX motion graphics — deployed immediately into live ad campaigns.',
        results: 'The high-velocity launch and diverse creative angles immediately resonated with B2B buyers, allowing the client to capture market share instantly with highly qualified, high-volume inquiries right out of the gate.',
      },
    },
    contact: {
      tag: 'Contact',
      title1: 'Get In',
      titleHighlight: 'Touch',
      subtitle: 'Ready to grow your brand? Let\'s start a conversation.',
      infoTitle: 'Contact Information',
      address: 'Address',
      email: 'Email',
      phone: 'Phone',
      quickResponse: 'Quick Response',
      quickResponseText: 'We typically respond within 24 hours.',
      emailDirect: 'Email Us Directly',
      formTitle: 'Send us a message',
      name: 'Name',
      emailLabel: 'Email',
      service: 'Service Required',
      selectService: 'Select a service',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      namePlaceholder: 'Your name',
      emailPlaceholder: 'your@email.com',
      messagePlaceholder: 'Tell us about your project...',
    },
    careers: {
      tag: 'Careers',
      title1: 'Join Our',
      titleHighlight: 'Team',
      subtitle: 'We are always looking for talented individuals who are passionate about AI, creativity, and marketing.',
      whyTitle: 'Why Work With Us?',
      perks: [
        { title: 'Opportunities to grow', desc: 'Learn and develop with industry-leading tools and mentors.' },
        { title: 'Creative environment', desc: 'Express yourself in a culture that values innovation.' },
        { title: 'Flexible work culture', desc: 'Work remotely or from our office. Your choice.' },
        { title: 'Amazing team', desc: 'Collaborate with talented, passionate professionals.' },
      ],
      openPositions: 'Open Positions',
      applyNow: 'Apply Now',
      close: 'Close',
      fullName: 'Full Name',
      coverLetter: 'Cover Letter',
      coverPlaceholder: "Tell us why you'd be a great fit...",
      submitApp: 'Submit Application',
      submitting: 'Submitting...',
    },
    blog: {
      tag: 'Blog',
      title1: 'Insights &',
      titleHighlight: 'Strategies',
      subtitle: 'We share valuable insights to help businesses grow.',
      featured: 'Featured',
      readArticle: 'Read article',
      readMore: 'Read more',
      backToBlog: 'Back to Blog',
      notFound: 'Post not found',
    },
    footer: {
      stayUpdated: 'Stay Updated',
      stayUpdatedSub: 'Get the latest updates, tips, and strategies in digital marketing.',
      subscribe: 'Subscribe',
      emailPlaceholder: 'Enter your email',
      tagline: 'Powered by AI. Driven by Results.',
      company: 'Company',
      servicesLabel: 'Services',
      resources: 'Resources',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      rights: 'All rights reserved.',
    },
  },
  ar: {
    dir: 'rtl',
    nav: {
      home: 'الرئيسية',
      about: 'من نحن',
      services: 'الخدمات',
      portfolio: 'أعمالنا',
      caseStudies: 'دراسات الحالة',
      blog: 'المدونة',
      careers: 'الوظائف',
      contact: 'تواصل معنا',
      consultation: 'استشارة مجانية',
      more: 'المزيد',
    },
    home: {
      badge: 'مدعوم بالذكاء الاصطناعي',
      title1: 'انمو أسرع مع',
      titleHighlight: 'التسويق بالذكاء الاصطناعي',
      title2: '',
      subtitle: 'زد ديجيتالز تساعد العلامات التجارية على التوسع من خلال محتوى ذكاء اصطناعي قوي وفيديوهات UGC جذابة واستراتيجيات إعلانية تحقق نتائج.',
      ctaPrimary: 'احصل على استشارة مجانية',
      ctaSecondary: 'عرض الأعمال',
      stat1: 'سنوات خبرة',
      stat2: 'متوسط زيادة العائد',
      stat3: 'مرة ظهور إعلاني',
      stat4: 'منصات إعلانية',
      servicesTitle: 'خدماتنا الأساسية',
      servicesTag: 'ماذا نقدم',
      svc1: 'إدارة وسائل التواصل الاجتماعي',
      svc1Desc: '٢٥ محتوى شهرياً. ريلز احترافية، منشورات، كتابة محتوى وإدارة يومية للقصص.',
      svc2: 'إبداعات إعلانية بالذكاء الاصطناعي',
      svc2Desc: '٢٥ فيديو إعلاني بالذكاء الاصطناعي شهرياً. تحليل المنافسين واستراتيجية وكتابة محتوى مخصص.',
      svc3: 'الإعلانات المدفوعة',
      svc3Desc: 'الباقة الكاملة للنمو. محتوى عضوي + إبداعات ذكاء اصطناعي + شراء إعلاني مكثف متعدد المنصات.',
      learnMore: 'اعرف المزيد',
      platformsTag: 'المنصات التي نعمل عليها',
      whyTag: 'لماذا زد ديجيتالز',
      whyTitle: 'نتائج تتحدث عن نفسها',
      whySubtitle: 'نجمع بين تقنيات الذكاء الاصطناعي المتطورة واستراتيجيات التسويق المثبتة لتقديم نتائج قابلة للقياس لكل عميل.',
      whyPoints: ['إنتاج محتوى بالذكاء الاصطناعي', 'نهج تسويقي قائم على البيانات', 'خبرة عبر منصات متعددة', 'التركيز على النمو الحقيقي للأعمال'],
      caseTag: 'دراسات الحالة',
      caseTitle: 'نتائج حقيقية. نمو حقيقي.',
      viewAll: 'عرض الكل',
      testimonialsTag: 'آراء العملاء',
      testimonialsTitle: 'ماذا يقول عملاؤنا',
      ctaTitle: 'مستعد لتنمية أعمالك؟',
      ctaSubtitle: 'ابدأ رحلتك مع زد ديجيتالز اليوم واختبر قوة التسويق بالذكاء الاصطناعي.',
      ctaBtn: 'ابدأ اليوم',
    },
    about: {
      tag: 'من نحن',
      title1: 'نبني علامات تجارية',
      titleHighlight: 'تهيمن',
      subtitle: 'زد ديجيتالز وكالة تسويق رقمي حديثة تركز على مساعدة العلامات التجارية على النمو باستخدام محتوى الذكاء الاصطناعي واستراتيجيات إعلانية ذكية.',
      storyTag: 'قصتنا',
      storyTitle: 'من الرؤية إلى الواقع',
      storyP1: 'تأسست بإيمان أن التسويق يجب أن يكون أذكى وليس أصعب، نشأت زد ديجيتالز عند تقاطع الذكاء الاصطناعي والسرد الإبداعي.',
      storyP2: 'أدركنا مبكراً أن مستقبل التسويق يكمن في الاستفادة من الذكاء الاصطناعي لإنشاء محتوى مخصص وعالي التحويل على نطاق واسع. اليوم نخدم أكثر من ٢٠٠ عميل في المملكة المتحدة وخارجها.',
      storyP3: 'يعمل فريقنا من العقول المبدعة وعلماء البيانات واستراتيجيي التسويق معاً لتقديم حملات لا تبدو رائعة فحسب بل تحقق أداءً متميزاً.',
      missionTitle: 'مهمتنا',
      missionText: 'مساعدة الشركات على النمو بشكل أسرع من خلال حلول تسويقية مبتكرة مدعومة بالذكاء الاصطناعي. نحن ملتزمون بتقديم نتائج قابلة للقياس تدفع نمو الأعمال الحقيقي.',
      visionTitle: 'رؤيتنا',
      visionText: 'أن نصبح وكالة رائدة معروفة بالإبداع والابتكار والنتائج القابلة للقياس. نتصور عالماً تتمتع فيه كل علامة تجارية بالوصول إلى تسويق على مستوى المؤسسات مدعوم بالذكاء الاصطناعي.',
      valuesTag: 'قيمنا',
      valuesTitle: 'ما يحركنا',
      values: [
        { title: 'الابتكار', desc: 'ندفع الحدود بحلول ذكاء اصطناعي وإبداعية متطورة.' },
        { title: 'الشفافية', desc: 'تواصل صادق وتقارير واضحة في كل خطوة.' },
        { title: 'الأداء', desc: 'كل استراتيجية مبنية حول نتائج قابلة للقياس.' },
        { title: 'الإبداع', desc: 'نمزج الفن والبيانات لإنشاء تجارب علامة تجارية لا تُنسى.' },
      ],
      teamTag: 'الفريق',
      teamTitle: 'تعرف على خبرائنا',
    },
    services: {
      tag: 'خدماتنا',
      title1: 'محتوى بالذكاء الاصطناعي.',
      titleHighlight: 'نمو حقيقي.',
      subtitle: 'من إنتاج فيديو بالذكاء الاصطناعي بكميات كبيرة إلى إعلانات مدفوعة شاملة — نقدم محرك المحتوى وخبرة شراء الوسائط التي تحتاجها علامتك التجارية للهيمنة.',
      whatsIncluded: 'ما يتضمنه',
      getStarted: 'ابدأ الآن',
      completePackage: 'باقة النمو الكاملة',
      platformsTag: 'المنصات',
      platformsTitle: 'ننمو عبر جميع المنصات الرئيسية',
      ctaTitle: 'مستعد للتوسع بالذكاء الاصطناعي؟',
      ctaSubtitle: 'دعنا نناقش أي باقة مناسبة لعلامتك التجارية ونبدأ في بناء محرك المحتوى الخاص بك.',
      ctaBtn: 'احجز مكالمة استراتيجية',
      svc1: {
        title: 'إدارة وسائل التواصل الاجتماعي',
        tagline: 'نمو عضوي شامل للتواصل الاجتماعي مدعوم بمحتوى احترافي عالي الكمية.',
        items: ['٢٥ محتوى شهرياً', '٢٠ ريلز احترافي', '٥ منشورات عالية الجودة', 'كتابة محتوى كاملة لكل قطعة', 'إدارة يومية للهايلايتس والقصص', 'بحث سوق واستراتيجية شاملة'],
      },
      svc2: {
        title: 'إبداعات إعلانية بالذكاء الاصطناعي',
        tagline: 'إبداعات فيديو عالية التحويل مُنتجة بالذكاء الاصطناعي ومصممة لتسويق الأداء.',
        items: ['٢٥ إبداع إعلاني احترافي شهرياً', 'تحليل عميق للمنافسين', 'بحث سوق واستراتيجية تسويقية كاملة', 'كتابة إعلانية مخصصة لكل إبداع', 'تنسيقات محسنة للأداء', 'تنويعات إبداعية متعددة المنصات'],
      },
      svc3: {
        title: 'الإعلانات المدفوعة',
        tagline: 'الحل النهائي للتوسع. نجمع بين المحتوى العضوي والإبداعات الذكية وشراء الوسائط المكثف.',
        items: ['كل ما في الخدمتين ٠١ و ٠٢', 'إدارة إعلانات مدفوعة مخصصة', 'حملات ميتا (إنستغرام/فيسبوك)', 'حملات إعلانات جوجل', 'حملات تيك توك وسناب شات', '٢٥ إبداع إعلاني حصري للحملات المدفوعة', 'تجديد كامل للهوية البصرية للعلامة التجارية', 'تصميم شعار جديد إذا لزم الأمر'],
      },
    },
    portfolio: {
      tag: 'أعمالنا',
      title1: 'فيديو بالذكاء الاصطناعي',
      titleHighlight: 'إنتاج',
      title2: 'معرض الأعمال',
      subtitle: 'إبداعات فيديو حقيقية مُنتجة بالذكاء الاصطناعي لعملائنا. مرر للمعاينة، اضغط للمشاهدة.',
      industry: 'القطاع',
      style: 'النمط',
      all: 'الكل',
      aiGenerated: 'مُنتج بالذكاء الاصطناعي',
      noResults: 'لا توجد فيديوهات تطابق الفلاتر المحددة.',
    },
    caseStudies: {
      tag: 'دراسات الحالة',
      title1: 'عائد استثمار مُثبت.',
      titleHighlight: 'نمو قابل للقياس.',
      subtitle: 'شاهد كيف حققت إبداعاتنا الإعلانية بالذكاء الاصطناعي واستراتيجيات شراء الوسائط عوائد حقيقية لعلامات تجارية حقيقية.',
      challenge: 'المشكلة',
      aiSolution: 'ما الذي قمنا به',
      results: 'النتائج',
      getResults: 'احصل على نتائج مثل هذه',
      ctaTitle: 'تريد هذه النتائج لعلامتك التجارية؟',
      ctaSubtitle: 'دعنا نريك كيف يمكن للإبداعات المُنتجة بالذكاء الاصطناعي أن تحول أداء إعلاناتك.',
      ctaBtn: 'احجز مكالمة استراتيجية',
      clientStudy: 'دراسة حالة عميل',
      labelClient: 'العميل',
      labelIndustry: 'القطاع',
      labelLocation: 'الموقع',
      labelService: 'الخدمة',
      labelCatalog: 'منتج في الكتالوج',
      labelRating: 'تقييم العملاء',
      labelFormat: 'نوع الإعلان',
      labelBookingRate: 'معدل الحجز',
      labelCpc: 'تكلفة النقرة',
      labelFullLaunch: 'الإطلاق الكامل',
      labelUnitLead: 'وحدة عميل B2B',
      labelFullStack: 'متكامل',
      labelFullStackServices: 'الخدمات',
      amxClient: 'AMX للطباعة المخصصة',
      amxTagline: 'إطلاق علامة تجارية متكامل',
      dhz: {
        headline: 'كيف حوّلنا إعلانات DHZ-Proshop على ميتا إلى محرك مبيعات عالي الأداء',
        client: 'DHZ-Proshop',
        industry: 'تجزئة إلكترونية متميزة — أدوات احترافية ومواد صناعية',
        location: 'بلجيكا',
        service: 'إعلانات ميتا — استراتيجية وإنتاج الإبداعات المرئية',
        problem: 'كان لدى DHZ-Proshop كتالوج ضخم من أكثر من ٤٠,٠٠٠ منتج وتقييم عملاء ٩.٦/١٠ — لكن إعلاناتهم على ميتا لم تكن تحوّل. الصور الثابتة للمنتجات فشلت في إيصال خبرة العلامة التجارية، سرعة الخدمات اللوجستية، أو حجم مخزونهم.',
        whatWeDid: 'استبدلنا جميع الإبداعات الثابتة بإعلانات فيديو عالية الجودة قائمة على السرد القصصي، مبنية على ثلاث ركائز: التواصل البشري (متحدث رسمي يجول في المستودع)، إلحاح الخدمات اللوجستية ("اطلب قبل الساعة ١٦:٠٠، يُشحن اليوم")، والإثبات الاجتماعي (تقييم ٩.٦/١٠ منسوج في المرئيات). نُشرت ضمن استراتيجية القمع الكامل لميتا.',
        results: 'زيادة ملحوظة في حجم المعاملات اليومية. نسبة نقر أعلى من السرد المرئي مقارنة بالصور الثابتة. استقرار ROAS عند مستوى مربح باستمرار. أوقات مشاهدة أطول وتذكّر أقوى للعلامة التجارية.',
        statSku: '+٤٠ ألف',
        statSkuLabel: 'منتج في الكتالوج',
        statRating: '٩.٦/١٠',
        statRatingLabel: 'تقييم العملاء',
        statFormat: 'إعلانات ميتا',
        statFormatLabel: 'نوع الإعلان',
      },
      gadget: {
        headline: 'كيف ساعدنا The Gadget Guys على اختراق الضوضاء الرقمية ودفع مبيعات حقيقية',
        client: 'The Gadget Guys',
        industry: 'خدمات تقنية وتجزئة — دعم تقني، تجميع أجهزة ألعاب، صيانة كمبيوتر',
        location: 'فورت واشنطن',
        service: 'إعلانات ميتا — استراتيجية وإنتاج الإبداعات المرئية',
        problem: 'كان The Gadget Guys يديرون إعلانات ميتا بصور مخزنة عامة لأجهزة الكمبيوتر. كانت الإبداعات مزدحمة وبلا اتصال شخصي ولم تكن تحوّل — مما أدى إلى هدر ميزانية الإعلانات وركود المبيعات.',
        whatWeDid: 'انتقلنا إلى محتوى فيديو عالي الطاقة وقائم على السرد القصصي يعرض فنيين حقيقيين يعملون على تجميعات فعلية. استخدمنا تنبيهات ديناميكية على الشاشة لخدمات محددة (إزالة الفيروسات، استعادة البيانات، تجميع أجهزة ألعاب مخصصة)، وأبرزنا موقعهم الفعلي ورقم هاتفهم لتوليد العملاء المحليين، وبنينا الفيديوهات حول إطار واضح للمشكلة والحل.',
        results: 'ارتفاع ملحوظ في معدلات التحويل لحجوزات الخدمة ومبيعات الأجهزة. خوارزمية ميتا فضّلت محتوى الفيديو، مما خفّض تكلفة النقرة. تحسّن ROAS عبر الحساب. زيادة في حركة المرور المباشرة والاستفسارات الهاتفية في فورت واشنطن.',
        statConv: '↑ تحويل',
        statConvLabel: 'معدل الحجز',
        statCpc: '↓ CPC',
        statCpcLabel: 'تكلفة النقرة',
        statFormat: 'إعلانات ميتا',
        statFormatLabel: 'نوع الإعلان',
      },
      amx: {
        headline: 'AMX للطباعة المخصصة — إطلاق علامة تجارية شامل في أقل من ١٠ ساعات',
        problem: 'احتاج العميل إلى إطلاق علامة تجارية رقمية كاملة من الصفر — بما في ذلك الهوية البصرية ووجود وسائل التواصل الاجتماعي والإبداعات الإعلانية — وتطلّب وقت تنفيذ سريع للغاية دون التضحية بالجودة أو الأداء.',
        whatWeDid: 'نفّذت زد ديجيتالز إطلاقاً كاملاً وشاملاً في أقل من ١٠ ساعات. صممنا شعارهم الجديد، وبنينا استراتيجيتهم الاجتماعية، وأنتجنا مجموعة متنوعة من الإبداعات بالذكاء الاصطناعي — من تجارب UGC إلى رسوم متحركة ثلاثية الأبعاد — تم نشرها فوراً في حملات إعلانية مباشرة.',
        results: 'الإطلاق السريع وزوايا الإبداع المتنوعة لاقت صدى فورياً لدى مشتري B2B، مما سمح للعميل بالاستحواذ على حصة السوق على الفور باستفسارات عالية الجودة وكبيرة الحجم منذ البداية.',
      },
    },
    contact: {
      tag: 'تواصل معنا',
      title1: 'تواصل',
      titleHighlight: 'معنا',
      subtitle: 'مستعد لتنمية علامتك التجارية؟ لنبدأ محادثة.',
      infoTitle: 'معلومات التواصل',
      address: 'العنوان',
      email: 'البريد الإلكتروني',
      phone: 'الهاتف',
      quickResponse: 'رد سريع',
      quickResponseText: 'نرد عادةً خلال ٢٤ ساعة.',
      emailDirect: 'راسلنا مباشرة',
      formTitle: 'أرسل لنا رسالة',
      name: 'الاسم',
      emailLabel: 'البريد الإلكتروني',
      service: 'الخدمة المطلوبة',
      selectService: 'اختر خدمة',
      message: 'الرسالة',
      send: 'إرسال الرسالة',
      sending: 'جارٍ الإرسال...',
      namePlaceholder: 'اسمك',
      emailPlaceholder: 'بريدك@email.com',
      messagePlaceholder: 'أخبرنا عن مشروعك...',
    },
    careers: {
      tag: 'الوظائف',
      title1: 'انضم إلى',
      titleHighlight: 'فريقنا',
      subtitle: 'نبحث دائماً عن أفراد موهوبين شغوفين بالذكاء الاصطناعي والإبداع والتسويق.',
      whyTitle: 'لماذا تعمل معنا؟',
      perks: [
        { title: 'فرص للنمو', desc: 'تعلم وتطور مع أدوات ومرشدين رائدين في الصناعة.' },
        { title: 'بيئة إبداعية', desc: 'عبر عن نفسك في ثقافة تقدر الابتكار.' },
        { title: 'ثقافة عمل مرنة', desc: 'اعمل عن بُعد أو من مكتبنا. اختيارك.' },
        { title: 'فريق رائع', desc: 'تعاون مع محترفين موهوبين وشغوفين.' },
      ],
      openPositions: 'الوظائف المتاحة',
      applyNow: 'قدم الآن',
      close: 'إغلاق',
      fullName: 'الاسم الكامل',
      coverLetter: 'رسالة التقديم',
      coverPlaceholder: 'أخبرنا لماذا ستكون مناسباً رائعاً...',
      submitApp: 'إرسال الطلب',
      submitting: 'جارٍ الإرسال...',
    },
    blog: {
      tag: 'المدونة',
      title1: 'رؤى و',
      titleHighlight: 'استراتيجيات',
      subtitle: 'نشارك رؤى قيمة لمساعدة الشركات على النمو.',
      featured: 'مميز',
      readArticle: 'اقرأ المقال',
      readMore: 'اقرأ المزيد',
      backToBlog: 'العودة للمدونة',
      notFound: 'المقال غير موجود',
    },
    footer: {
      stayUpdated: 'ابقَ على اطلاع',
      stayUpdatedSub: 'احصل على آخر التحديثات والنصائح والاستراتيجيات في التسويق الرقمي.',
      subscribe: 'اشترك',
      emailPlaceholder: 'أدخل بريدك الإلكتروني',
      tagline: 'مدعوم بالذكاء الاصطناعي. مدفوع بالنتائج.',
      company: 'الشركة',
      servicesLabel: 'الخدمات',
      resources: 'الموارد',
      privacy: 'سياسة الخصوصية',
      terms: 'شروط الخدمة',
      rights: 'جميع الحقوق محفوظة.',
    },
  },
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('zs-lang') || 'en');

  useEffect(() => {
    localStorage.setItem('zs-lang', lang);
    document.documentElement.dir = translations[lang].dir;
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key) => {
    const keys = key.split('.');
    let val = translations[lang];
    for (const k of keys) {
      val = val?.[k];
      if (val === undefined) return key;
    }
    return val;
  };

  const toggleLang = () => setLang(lang === 'en' ? 'ar' : 'en');

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, toggleLang, isRTL: lang === 'ar' }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be inside LanguageProvider');
  return ctx;
}
